import { useEffect, useState } from 'react'

import {
  useRunVisitorSummaryInsightApi,
  useVisitorSummaryInsightsApi
} from '@/common/api/modules/customer'

/**
 * 訪問者サマリのAI示唆の状態（2026-10-04 追加・SMART_HUB_DEV-127）。
 *
 * コンポーネントと同じファイルに置くと Fast Refresh が効かなくなるため分けている。
 */

/** 生成の段取り。AIを待つ間に何をしているかを見せる */
export const VISITOR_INSIGHT_STEPS = ['集計しています', 'AIが解釈しています', '保存しています']

/** 生成にかかる目安（秒）。実測 16〜19 秒に余裕を持たせた値。残り時間の表示に使う */
export const VISITOR_INSIGHT_ETA_SEC = 30

/** 「AIが解釈中」へ進める目安の時間。実際の進捗は取れないので見た目のためだけ */
const STEP2_AFTER_MS = 1500

export const useVisitorInsight = (siteId: number, days: number) => {
  const { data, isLoading, mutate } = useVisitorSummaryInsightsApi({ site_id: siteId })
  const [selected, setSelected] = useState<number | null>(null)
  const [step, setStep] = useState(0)
  const [elapsed, setElapsed] = useState(0)
  const [error, setError] = useState<string | null>(null)

  const { trigger, isMutating } = useRunVisitorSummaryInsightApi({
    onSuccess: (res) => {
      setStep(0)
      if (res && res.ok === false) {
        // 失敗は世代にならない。理由をそのまま出す（空の示唆を出さない）
        setError(res.error ?? '示唆を作れませんでした')
        return
      }
      setError(null)
      setSelected(res?.insight?.id ?? null)
      mutate()
    },
    onError: () => {
      setStep(0)
      setError('示唆を作れませんでした。時間をおいて再実行してください')
    }
  })

  // 実行中の経過秒。残り時間の目安を出すために数える
  useEffect(() => {
    if (!isMutating) return
    const timer = window.setInterval(() => setElapsed((s) => s + 1), 1000)
    return () => window.clearInterval(timer)
  }, [isMutating])

  const items = data?.items ?? []

  return {
    items,
    current: items.find((x) => x.id === selected) ?? items[0],
    keepGenerations: data?.keep_generations ?? 10,
    isLoading,
    isMutating,
    step,
    elapsed,
    error,
    setSelected,
    run: () => {
      setError(null)
      setElapsed(0)
      setStep(1)
      window.setTimeout(() => setStep(2), STEP2_AFTER_MS)
      trigger({ site_id: siteId, days })
    }
  }
}

export type VisitorInsightState = ReturnType<typeof useVisitorInsight>
